/**
 * Series manager component for managing essay membership in series.
 * 
 * Features:
 * - Dropdown to select/create series
 * - Order number input for position in series
 * - Remove from series button
 * - Link to view full series page
 * - Display series navigation (previous/next)
 * 
 * Called by:
 * - EssayDetailPage for managing series membership
 */
import { useState } from 'react';
import {
  Select,
  InputNumber,
  Button,
  Space,
  Card,
  Typography,
  Divider,
  Input,
  message,
  Empty,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  OrderedListOutlined,
  DeleteOutlined,
  LeftOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getAllSeries,
  createSeries,
  addEssayToSeries,
  removeEssayFromSeries,
} from '../services/api';
import type { EssaySeriesInfo, User } from '@compendium/shared';

const { Text, Title } = Typography;

// =============================================================================
// Props Interface
// =============================================================================

interface SeriesManagerProps {
  /** Essay slug for API calls */
  slug: string;
  /** Current series info for this essay */
  seriesInfo: EssaySeriesInfo[];
  /** Current user for permission checks */
  user: User;
  /** Callback when series membership changes */
  onSeriesChange?: () => void;
}

// =============================================================================
// Main Component
// =============================================================================

/**
 * Series manager component.
 * Allows adding/removing essays from series and setting order.
 */
function SeriesManager({
  slug,
  seriesInfo,
  user,
  onSeriesChange,
}: SeriesManagerProps) {
  const queryClient = useQueryClient();
  const [selectedSeriesId, setSelectedSeriesId] = useState<number | null>(null);
  const [orderIndex, setOrderIndex] = useState<number>(0);
  const [newSeriesName, setNewSeriesName] = useState('');
  const isAuthor = user.role === 'author';

  // Fetch all series
  const { data: allSeries = [], isLoading: seriesLoading } = useQuery({
    queryKey: ['series'],
    queryFn: getAllSeries,
  });

  // Create series mutation
  const createSeriesMutation = useMutation({
    mutationFn: (name: string) => createSeries({ name }),
    onSuccess: (newSeries) => {
      message.success(`Series "${newSeries.name}" created`);
      queryClient.invalidateQueries({ queryKey: ['series'] });
      setNewSeriesName('');
      // Auto-select the newly created series
      setSelectedSeriesId(newSeries.id);
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to create series');
    },
  });

  // Add to series mutation
  const addToSeriesMutation = useMutation({
    mutationFn: ({ seriesId, orderIndex }: { seriesId: number; orderIndex: number }) =>
      addEssayToSeries(slug, seriesId, orderIndex),
    onSuccess: () => {
      message.success('Essay added to series');
      queryClient.invalidateQueries({ queryKey: ['essaySeries', slug] });
      onSeriesChange?.();
      setSelectedSeriesId(null);
      setOrderIndex(0);
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to add to series');
    },
  });

  // Remove from series mutation
  const removeFromSeriesMutation = useMutation({
    mutationFn: (seriesId: number) => removeEssayFromSeries(slug, seriesId),
    onSuccess: () => {
      message.success('Essay removed from series');
      queryClient.invalidateQueries({ queryKey: ['essaySeries', slug] });
      onSeriesChange?.();
    },
    onError: (error: Error) => {
      message.error(error.message || 'Failed to remove from series');
    },
  });

  /**
   * Handle adding essay to selected series.
   */
  function handleAddToSeries() {
    if (!selectedSeriesId) return;
    addToSeriesMutation.mutate({ seriesId: selectedSeriesId, orderIndex });
  }

  /**
   * Handle creating a new series.
   */
  function handleCreateSeries() {
    if (!newSeriesName.trim()) return;
    createSeriesMutation.mutate(newSeriesName.trim());
  }

  // Series IDs the essay is already in
  const currentSeriesIds = seriesInfo.map((si) => si.series.id);

  // Filter out series the essay is already in
  const availableSeries = allSeries.filter((s) => !currentSeriesIds.includes(s.id));

  // Series options for Select
  const seriesOptions = availableSeries.map((s) => ({
    value: s.id,
    label: s.name,
  }));

  // Custom dropdown render with create option
  const dropdownRender = (menu: React.ReactNode) => (
    <>
      {menu}
      {isAuthor && (
        <>
          <Divider style={{ margin: '8px 0' }} />
          <Space style={{ padding: '0 8px 8px' }}>
            <Input
              placeholder="New series name"
              value={newSeriesName}
              onChange={(e) => setNewSeriesName(e.target.value)}
              onPressEnter={handleCreateSeries}
              size="small"
              style={{ flex: 1 }}
            />
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={handleCreateSeries}
              loading={createSeriesMutation.isPending}
              size="small"
            >
              Create
            </Button>
          </Space>
        </>
      )}
    </>
  );

  return (
    <div style={{ padding: '16px 0' }}>
      <Space style={{ marginBottom: 16 }}>
        <OrderedListOutlined />
        <Title level={5} style={{ margin: 0 }}>
          Series
        </Title>
      </Space>

      {/* Current series memberships */}
      {seriesInfo.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          {seriesInfo.map((info) => (
            <Card
              key={info.series.id}
              size="small"
              style={{ marginBottom: 8, background: '#242424' }}
            >
              {/* Series name and position */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 8,
                }}
              >
                <Space>
                  <Link
                    to={`/reviewer/series/${info.series.slug}`}
                    style={{ fontWeight: 500, color: '#e0bf4a' }}
                  >
                    {info.series.name}
                  </Link>
                  <Text type="secondary">
                    (Part {info.orderIndex + 1} of {info.totalInSeries})
                  </Text>
                </Space>
                {isAuthor && (
                  <Popconfirm
                    title="Remove from series?"
                    onConfirm={() => removeFromSeriesMutation.mutate(info.series.id)}
                    okText="Remove"
                    cancelText="Cancel"
                  >
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<DeleteOutlined />}
                      loading={removeFromSeriesMutation.isPending}
                    />
                  </Popconfirm>
                )}
              </div>

              {/* Navigation to prev/next in series */}
              <Space size="large" style={{ width: '100%', justifyContent: 'center' }}>
                {info.previousEssay ? (
                  <Link to={`/reviewer/essays/${info.previousEssay.slug}`}>
                    <Button type="text" size="small" icon={<LeftOutlined />}>
                      {info.previousEssay.title.length > 30
                        ? info.previousEssay.title.substring(0, 30) + '...'
                        : info.previousEssay.title}
                    </Button>
                  </Link>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    (First in series)
                  </Text>
                )}
                {info.nextEssay ? (
                  <Link to={`/reviewer/essays/${info.nextEssay.slug}`}>
                    <Button type="text" size="small">
                      {info.nextEssay.title.length > 30
                        ? info.nextEssay.title.substring(0, 30) + '...'
                        : info.nextEssay.title}
                      <RightOutlined />
                    </Button>
                  </Link>
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    (Last in series)
                  </Text>
                )}
              </Space>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state */}
      {seriesInfo.length === 0 && (
        <Empty
          description="Not part of any series"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Add to series (author only) */}
      {isAuthor && (
        <div style={{ background: '#242424', padding: 12, borderRadius: 8 }}>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>
            Add to Series
          </Text>
          <Space direction="vertical" style={{ width: '100%' }}>
            <Select
              placeholder="Select a series"
              value={selectedSeriesId}
              onChange={setSelectedSeriesId}
              options={seriesOptions}
              loading={seriesLoading}
              style={{ width: '100%' }}
              dropdownRender={dropdownRender}
              allowClear
            />
            {selectedSeriesId && (
              <Space>
                <Text>Position:</Text>
                <InputNumber
                  min={0}
                  value={orderIndex}
                  onChange={(val) => setOrderIndex(val || 0)}
                  size="small"
                  style={{ width: 80 }}
                />
                <Button
                  type="primary"
                  size="small"
                  onClick={handleAddToSeries}
                  loading={addToSeriesMutation.isPending}
                >
                  Add
                </Button>
              </Space>
            )}
          </Space>
        </div>
      )}
    </div>
  );
}

export default SeriesManager;
